/**
 * This context is used to store the state of the opening which is currently stored in the the zustand store
 * But this is a global state and we want to use it in the opening page alone
 * Eventually everything from the zustand store will be moved to this context
 */

import {
  SourcingHiringGoalWithMetrics,
  SourcingOpeningDetailAnalyticsData,
  SourcingOpeningDetailsResponse,
} from 'api-clients/monolith';
import { createContext, useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

type OpeningContextType = {
  showAggregateData: boolean;
  setShowAggregateData: (showAggregateData: boolean) => void;
  analyticsData?: SourcingOpeningDetailAnalyticsData;
  setAnalyticsData: (analytics: SourcingOpeningDetailAnalyticsData) => void;
  showAggregateHiresCheckbox: boolean;
  setShowAggregateHiresCheckbox: (showCheckbox: boolean) => void;
  hiringGoal?: SourcingHiringGoalWithMetrics;
  setHiringGoal: (hiringGoal: SourcingHiringGoalWithMetrics) => void;
  openingTitle?: SourcingOpeningDetailsResponse['title'];
  setOpeningTitle: (title: SourcingOpeningDetailsResponse['title']) => void;
  hiringGoalUrl?: string;
  setHiringGoalUrl: (url: string) => void;
  openingUrl?: string;
  setOpeningUrl: (url: string) => void;
  currentHiringGoalId?: string;
  hasActiveHiringGoal: boolean;
  setHasActiveHiringGoal: (hasActiveHiringGoal: boolean) => void;
  /** Whether the opening is active. Used to show inactive opening banner. */
  isOpeningActive: boolean;
  setIsOpeningActive: (active: boolean) => void;
  showAggregateHires: boolean;
  setShowAggregateHires: (showAggregateHires: boolean) => void;
  showAggregateSpend: boolean;
  setShowAggregateSpend: (showAggregateSpend: boolean) => void;
  /* The color of the budget automation banner. This is used to determine the color of the budget card in the analytics section. */
  budgetAutomationBannerColor?: 'blue' | 'rose';
  setBudgetAutomationBannerColor: (color: 'blue' | 'rose' | undefined) => void;
  isSourcingEnabled: boolean;
  setIsSourcingEnabled: (sourcingEnabled: boolean) => void;
  /**
   * False until opening detail (funnel) has been written into this context from the API.
   * Prevents trusting default true values for isOpeningActive / isSourcingEnabled before sync.
   */
  openingDetailApiDataReady: boolean;
  setOpeningDetailApiDataReady: (ready: boolean) => void;
};

export const OpeningContext = createContext<OpeningContextType | undefined>(
  undefined,
);

export function OpeningProvider({ children }: { children: React.ReactNode }) {
  const { openingSlug } = useParams();
  const [searchParams] = useSearchParams();
  const currentHiringGoalId = searchParams.get('hiring_goal_id') ?? undefined;

  const [showAggregateData, setShowAggregateData] = useState(true);
  const [showAggregateHires, setShowAggregateHires] = useState(true);
  const [showAggregateSpend, setShowAggregateSpend] = useState(false);
  const [analyticsData, setAnalyticsData] =
    useState<SourcingOpeningDetailAnalyticsData>();
  const [showAggregateHiresCheckbox, setShowAggregateHiresCheckbox] =
    useState(false);
  const [hiringGoal, setHiringGoal] = useState<SourcingHiringGoalWithMetrics>();
  const [openingUrl, setOpeningUrl] = useState<string>();
  const [openingTitle, setOpeningTitle] =
    useState<SourcingOpeningDetailsResponse['title']>();
  const [hiringGoalUrl, setHiringGoalUrl] = useState<string>();
  const [hasActiveHiringGoal, setHasActiveHiringGoal] =
    useState<SourcingOpeningDetailsResponse['has_active_hiring_goal']>(false);
  const [isOpeningActive, setIsOpeningActive] = useState(true);
  const [budgetAutomationBannerColor, setBudgetAutomationBannerColor] =
    useState<'blue' | 'rose'>();
  const [isSourcingEnabled, setIsSourcingEnabled] = useState(true);
  const [openingDetailApiDataReady, setOpeningDetailApiDataReady] =
    useState(false);

  useEffect(() => {
    setOpeningDetailApiDataReady(false);
  }, [openingSlug]);

  return (
    <OpeningContext.Provider
      value={{
        showAggregateData,
        setShowAggregateData,
        analyticsData,
        setAnalyticsData,
        showAggregateHiresCheckbox,
        setShowAggregateHiresCheckbox,
        hiringGoal,
        setHiringGoal,
        openingUrl,
        setOpeningUrl,
        openingTitle,
        setOpeningTitle,
        hiringGoalUrl,
        setHiringGoalUrl,
        currentHiringGoalId,
        hasActiveHiringGoal,
        setHasActiveHiringGoal,
        isOpeningActive,
        setIsOpeningActive,
        showAggregateHires,
        setShowAggregateHires,
        showAggregateSpend,
        setShowAggregateSpend,
        budgetAutomationBannerColor,
        setBudgetAutomationBannerColor,
        isSourcingEnabled,
        setIsSourcingEnabled,
        openingDetailApiDataReady,
        setOpeningDetailApiDataReady,
      }}
    >
      {children}
    </OpeningContext.Provider>
  );
}

export function useOpeningContext() {
  const context = useContext(OpeningContext);

  if (!context) {
    throw new Error('useOpeningContext must be used within an OpeningProvider');
  }

  return context;
}
